import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { processGifts, extractCartItemCollections } from '@theme/auto-gift-processor';
import { isWarrantyItem, normalizeWarrantyCart } from '@theme/cart-warranty';

class CartDrawer extends HTMLElement {
  #rendering = false;
  #normalizingWarranties = false;

  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', this.backdropClickHandler);

    this.checkoutButton.addEventListener('click', this.checkoutButtonClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);

    this.#normalizeWarranties({ notify: true });

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.checkoutButton.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    if (event.target.closest('dialog[open]')) return;

    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  #updateAutoGiftsMapping(autoGiftsHtml) {
    if (!autoGiftsHtml) return;

    const parsed = new DOMParser().parseFromString(autoGiftsHtml, 'text/html');
    const newScript = parsed.querySelector('[data-ref="cart-item-collections"]');
    const currentScript = document.querySelector('[data-ref="cart-item-collections"]');
    if (newScript && currentScript) currentScript.textContent = newScript.textContent;
  }

  async renderCartContents(parsedState) {
    if (this.#rendering) return;
    this.#rendering = true;

    try {
      const sectionId = this.dataset.sectionId;

      const newAutoGiftsHtml = parsedState.sections?.['auto-gifts'];
      const newAutoGifts = newAutoGiftsHtml ? new DOMParser().parseFromString(newAutoGiftsHtml, 'text/html') : null;
      const newCollections = newAutoGifts ? extractCartItemCollections(newAutoGifts) : {};

      const giftResult = await processGifts(sectionId, newCollections);

      if (giftResult?.sections) {
        parsedState = { ...parsedState, sections: { ...parsedState.sections, ...giftResult.sections }, itemCount: giftResult.itemCount ?? parsedState.itemCount };
      }

      const warrantyResult = await this.#normalizeWarranties({ notify: false });
      if (warrantyResult?.sections) {
        parsedState = { ...parsedState, sections: { ...parsedState.sections, ...warrantyResult.sections }, itemCount: warrantyResult.itemCount ?? parsedState.itemCount };
      }

      const sectionHtml = parsedState.sections?.[sectionId];
      if (!sectionHtml) return;

      const finalCartDrawer = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const currentCartDrawer = this.querySelector('.cart-drawer__inner');
      const nextInner = finalCartDrawer.querySelector('.cart-drawer__inner');
      if (!currentCartDrawer || !nextInner) return;

      currentCartDrawer.innerHTML = nextInner.innerHTML;

      this.#updateAutoGiftsMapping(parsedState.sections?.['auto-gifts']);

      setTimeout(() => {
        if (parsedState.itemCount == 0) {
          this.classList.add('cart-drawer--empty');
        } else {
          this.classList.remove('cart-drawer--empty');
        }
      });

      this.#syncCartIconCount(parsedState);
    } finally {
      this.#rendering = false;
    }
  }

  async #normalizeWarranties({ notify = false } = {}) {
    if (this.#normalizingWarranties || this.#rendering && notify) return null;
    this.#normalizingWarranties = true;

    try {
      return await normalizeWarrantyCart({
        sections: [this.dataset.sectionId, 'auto-gifts'].join(','),
        sectionsUrl: window.location.pathname,
        notify,
      });
    } finally {
      this.#normalizingWarranties = false;
    }
  }

  #syncCartIconCount(parsedState) {
    const cartIcon = document.querySelector('cart-icon');
    if (!cartIcon) return;

    const countEl = cartIcon.querySelector('[data-ref="cart-count"]');
    if (!countEl) return;

    if (typeof parsedState.itemCount === 'number') {
      countEl.textContent = parsedState.itemCount;
      return;
    }

    cartIcon.ensureCartCountIsCorrect();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  #updateLineItemQuantity(line, quantity) {
    this.#enableLoading();

    const extra = {
      sections: [this.closest('cart-drawer').dataset.sectionId, 'auto-gifts'].join(','),
      sections_url: window.location.pathname,
    };

    this.#lineChanges(line, quantity)
      .then((changes) => this.#applyLineChanges(changes, extra))
      .then((parsedResponseText) => {
        if (parsedResponseText?.errors) {
          const message = Array.isArray(parsedResponseText.errors)
            ? parsedResponseText.errors.join(', ')
            : parsedResponseText.errors;
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: message } }));
          return;
        }

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: parsedResponseText.item_count,
              source: 'cart-drawer-item',
              sections: parsedResponseText.sections,
            },
          ),
        );
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.#disableLoading();
      });
  }

  async #lineChanges(line, quantity) {
    const changes = [];
    const lineKey = this.dataset.lineKey;
    const qty = Number(quantity);

    if (qty === 0) {
      const warrantyKeys = new Set();
      const fromDom =
        this.dataset.warrantyLineKey ||
        this.closest('.cart-item-group')?.querySelector('cart-item-warranty')?.dataset.lineKey;

      if (fromDom) warrantyKeys.add(fromDom);

      for (const key of await this.#warrantyKeysFromCart(lineKey)) {
        warrantyKeys.add(key);
      }

      for (const key of warrantyKeys) {
        changes.push({ id: key, quantity: 0 });
      }
    }

    if (lineKey) {
      changes.push({ id: lineKey, quantity: qty });
    } else {
      changes.push({ line, quantity: qty });
    }

    return changes;
  }

  async #warrantyKeysFromCart(parentKey) {
    if (!parentKey) return [];

    try {
      const cartUrl = Theme.routes.cart_url.endsWith('.js') ? Theme.routes.cart_url : `${Theme.routes.cart_url}.js`;
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      if (!Array.isArray(cart.items)) return [];

      return cart.items
        .filter((item) => isWarrantyItem(item) && item.properties?._warranty_for === parentKey)
        .map((item) => item.key);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async #applyLineChanges(changes, extra) {
    let lastResponse = null;
    const changeUrl = Theme.routes.cart_change_url.endsWith('.js')
      ? Theme.routes.cart_change_url
      : `${Theme.routes.cart_change_url}.js`;

    for (let index = 0; index < changes.length; index++) {
      const change = changes[index];
      const isLast = index === changes.length - 1;
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...change, ...extra }),
      });
      lastResponse = await response.json();

      if (lastResponse.errors && isLast) return lastResponse;
    }

    return lastResponse;
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
